import React, { useEffect, useRef, useState } from "react";
import { ResizableBox } from "react-resizable";

export interface TrackWrapperProps {
  children: React.ReactNode;
  /**
   * Callback function that is triggered during a pan operation.
   * @param deltaY The vertical distance in pixels moved since the last event.
   */
  onPan: (deltaY: number) => void;
  /** Optional: Callback for when a pan operation starts. */
  onPanStart?: () => void;
  /** Optional: Callback for when a pan operation ends. */
  onPanEnd?: () => void;
  // Add other props like className, style, etc. if needed
  scrollIntoView?: boolean;
  trackWidth: number;
  chartHeight: number;
  onResize?: (event: React.SyntheticEvent, data: any) => void;
  editMode?: boolean;
  trackClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  minWidth?: number;
  onZoom?: (zoomFactor: number, mouseY: number) => void;
  onSetIsPanning: (isPanning: boolean) => void;
  mouseWheelZoom?: boolean;
  // Add start and end depth to props for zoom limit calculation
  startDepth: number;
  endDepth: number;
}

const TrackWrapper: React.FC<TrackWrapperProps> = ({
  scrollIntoView,
  children,
  trackWidth,
  chartHeight,
  onResize,
  editMode,
  trackClick,
  minWidth,
  onZoom,
  onPan,
  onPanStart,
  onPanEnd,
  onSetIsPanning,
  mouseWheelZoom,
  startDepth,
  endDepth,
}) => {
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollIntoView && fieldRef.current) {
      fieldRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [scrollIntoView]);

  useEffect(() => {
    const element = fieldRef.current;
    if (!element) return;

    const onWheel = (e: WheelEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!onZoom) return;

      // We must call preventDefault() because this listener is not passive.

      if (mouseWheelZoom) {
        const MIN_DEPTH_RANGE = 0.00001;
        const currentRange = endDepth - startDepth;

        // Zoom In
        if (e.deltaY > 0) {
          if (currentRange / 1.1 > MIN_DEPTH_RANGE) {
            const rect = element.getBoundingClientRect();
            const mouseY = e.clientY - (rect?.top ?? 0);
            onZoom(1.1, mouseY);
          }
        }
        // Zoom Out
        else if (e.deltaY < 0) {
          const rect = element.getBoundingClientRect();
          const mouseY = e.clientY - (rect?.top ?? 0);
          onZoom(0.9, mouseY);
        }
      } else {
        // Pan up and down
        const deltaY = e.deltaY;
        if (deltaY !== 0) {
          onSetIsPanning(true);
          onPan(-deltaY); // Invert delta for panning
          e.stopPropagation();
        }
      }
    };

    // Add the event listener with the passive option set to false.
    element.addEventListener("wheel", onWheel, { passive: false });

    // Return a cleanup function to remove the listener when the component unmounts.
    return () => {
      element.removeEventListener("wheel", onWheel);
    };
  }, [onZoom, mouseWheelZoom, startDepth, endDepth, onPan, onSetIsPanning]);

  // State to track whether a pan operation is active
  const [isPanning, setIsPanning] = useState(false);
  // Ref to store the last mouse Y position without causing re-renders
  const lastY = useRef(0);
  const animationFrameId = useRef<number | null>(null);
  const panDeltaY = useRef(0);

  // Drag-vs-click helpers
  const isMouseDownRef = useRef(false);
  const dragStartedRef = useRef(false);
  const startYRef = useRef(0);
  const lastYRef = useRef(0);
  const CLICK_MOVE_THRESHOLD_PX = 4;

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isMouseDownRef.current = true;
    dragStartedRef.current = false;
    startYRef.current = e.clientY;
    lastYRef.current = e.clientY;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMouseDownRef.current) return;

    const dy = e.clientY - lastYRef.current;
    lastYRef.current = e.clientY;

    // Only start panning after threshold
    if (!dragStartedRef.current) {
      const total = Math.abs(e.clientY - startYRef.current);
      if (total < CLICK_MOVE_THRESHOLD_PX) return;

      dragStartedRef.current = true;
      onPanStart?.();
      onSetIsPanning(true);
    }

    // Perform pan
    onPan(-dy);
  };

  const finishPan = () => {
    if (dragStartedRef.current) {
      onPanEnd?.();
      onSetIsPanning(false);
    }
    isMouseDownRef.current = false;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const wasDrag = dragStartedRef.current;
    finishPan();

    // If it wasn't a drag, treat as a click
    if (!wasDrag) {
      trackClick?.(e);
    }
  };

  const handleMouseLeave = () => {
    // Cancel pan when leaving the area
    finishPan();
  };

  return (
    <ResizableBox
      width={trackWidth}
      height={chartHeight}
      axis="x"
      onResize={onResize}
      resizeHandles={editMode ? ["e"] : []}
      minConstraints={[minWidth ?? 150, chartHeight]}
      maxConstraints={[300, chartHeight]}
    >
      <div
        className={`track ${editMode ? "selected-track-outline" : ""}`}
        ref={fieldRef}
        style={{
          overflow: "visible",
          cursor: isPanning ? "grabbing" : "grab",
          userSelect: "none",
        }}
        // Replace direct onClick with guarded handlers
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </ResizableBox>
  );
};

export default TrackWrapper;